import { useState } from "react";
import { Link as RouterLink, useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  Stack,
  Heading,
  Text,
  FormControl,
  FormLabel,
  FormErrorMessage,
  Input,
  InputGroup,
  InputRightElement,
  IconButton,
  Button,
  Link,
  Divider,
  Alert,
  AlertIcon,
  useToast,
} from "@chakra-ui/react";
import { Eye, EyeOff } from "../../../shared/icons";
import { trackEvent } from "../../../shared/lib/analytics";
import { loginSchema } from "../schemas/authSchemas";
import { useLoginMutation } from "../hooks/useLoginMutation";
import GoogleLoginButton from "../components/GoogleLoginButton";
import { authApi } from "../api/authApi";
import { lerVerificadorGoogle, limparVerificadorGoogle } from "../lib/googleVerifier";

// Motivos com que o backend devolve para o login quando o "Entrar com Google" não deu
// certo (?google=...).
const ERROS_GOOGLE = {
  cancelado: { status: "info", texto: "Login com Google cancelado." },
  inativo: { status: "error", texto: "Esta conta está desativada. Fale com o suporte." },
  email_nao_verificado: {
    status: "error",
    texto: "O e-mail desta conta Google não está verificado. Use outra conta ou entre com e-mail e senha.",
  },
  indisponivel: { status: "warning", texto: "O login com Google não está disponível no momento." },
  vincular: {
    status: "info",
    texto:
      "Já existe uma conta MedStation com este e-mail. Entre com a sua senha abaixo para vincular o Google a ela — depois disso, o botão do Google passa a funcionar. Se esqueceu a senha, redefina em \"Esqueceu sua senha?\" e depois clique em \"Continuar com Google\" de novo.",
  },
  erro: { status: "error", texto: "Não foi possível entrar com Google. Tente novamente." },
};

export default function LoginPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const toast = useToast();
  // ProtectedRoute manda para cá com a página pedida em state.from (ex.: o link do e-mail
  // de fim de trial, /perfil?assinar=1); depois do login, volta para ela.
  const from = location.state?.from;
  const destino = from?.pathname ? `${from.pathname}${from.search ?? ""}` : "/dashboard";
  const [showPassword, setShowPassword] = useState(false);
  const loginMutation = useLoginMutation();

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm({ resolver: zodResolver(loginSchema), defaultValues: { email: "", password: "" } });

  const sessaoExpirou = searchParams.get("sessionExpired") === "1";
  const erroGoogle = ERROS_GOOGLE[searchParams.get("google")];

  // Vínculo pendente do "Entrar com Google" (?google=vincular&link=...): só se completa com
  // o login por senha desta conta, no mesmo navegador que passou pelo Google.
  const linkGoogle = searchParams.get("google") === "vincular" ? searchParams.get("link") : null;

  const vincularGoogle = async () => {
    const verifier = lerVerificadorGoogle();
    if (!linkGoogle || !verifier) return;
    try {
      await authApi.googleConfirmLink(linkGoogle, verifier);
      toast({ title: "Conta Google vinculada", status: "success", duration: 4000 });
    } catch (error) {
      toast({
        title: "Não foi possível vincular o Google",
        description: error.response?.data?.erros?.[0]?.mensagem ?? error.response?.data?.mensagem,
        status: "warning",
        duration: 6000,
        isClosable: true,
      });
    } finally {
      limparVerificadorGoogle();
    }
  };

  const onSubmit = (data) => {
    loginMutation.mutate(data, {
      onSuccess: async () => {
        trackEvent("login");
        await vincularGoogle();
        navigate(destino);
      },
      onError: (error) => {
        toast({
          title: "Não foi possível entrar",
          description: error.response?.data?.mensagem || "Verifique seu e-mail e senha.",
          status: "error",
          duration: 5000,
          isClosable: true,
        });
      },
    });
  };

  return (
    <Stack spacing={6}>
      <Stack spacing={1} textAlign="center">
        <Heading size="md">Bem-vindo de volta</Heading>
        <Text color="app-muted-fg" fontSize="sm">
          Entre para acessar seus recebíveis
        </Text>
      </Stack>

      {sessaoExpirou ? (
        <Alert status="warning" borderRadius="md" fontSize="sm">
          <AlertIcon />
          Sua sessão expirou. Faça login novamente.
        </Alert>
      ) : null}

      {erroGoogle ? (
        <Alert status={erroGoogle.status} borderRadius="md" fontSize="sm">
          <AlertIcon />
          {erroGoogle.texto}
        </Alert>
      ) : null}

      <GoogleLoginButton />

      <form onSubmit={handleSubmit(onSubmit)} noValidate>
        <Stack spacing={4}>
          <FormControl isInvalid={Boolean(errors.email)}>
            <FormLabel htmlFor="email">E-mail</FormLabel>
            <Input id="email" type="email" autoComplete="email" autoFocus {...register("email")} />
            <FormErrorMessage>{errors.email?.message}</FormErrorMessage>
          </FormControl>

          <FormControl isInvalid={Boolean(errors.password)}>
            <FormLabel htmlFor="password">Senha</FormLabel>
            <InputGroup>
              <Input
                id="password"
                type={showPassword ? "text" : "password"}
                autoComplete="current-password"
                {...register("password")}
              />
              <InputRightElement>
                <IconButton
                  variant="ghost"
                  size="sm"
                  icon={showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                  onClick={() => setShowPassword((v) => !v)}
                  aria-label={showPassword ? "Ocultar senha" : "Mostrar senha"}
                />
              </InputRightElement>
            </InputGroup>
            <FormErrorMessage>{errors.password?.message}</FormErrorMessage>
          </FormControl>

          <Stack spacing={4}>
            <Button type="submit" size="lg" isLoading={loginMutation.isPending}>
              Entrar
            </Button>
            <Link as={RouterLink} to="/reset-password" color="accent-text" fontSize="sm" textAlign="center">
              Esqueceu sua senha?
            </Link>
          </Stack>
        </Stack>
      </form>

      <Stack spacing={4}>
        <Divider />
        <Text textAlign="center" fontSize="sm">
          Ainda não tem conta?{" "}
          <Link as={RouterLink} to="/auth/signup" color="accent-text" fontWeight="semibold">
            Criar conta
          </Link>
        </Text>
      </Stack>
    </Stack>
  );
}
